import { useMediaQuery, useTheme } from '@mui/material';
import { createContext, useContext, ReactNode, useState, useEffect } from 'react';

// Breakpoint types matching MUI
type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// Define device types
type DeviceType = 'mobile' | 'tablet' | 'desktop';

// Define responsive context interface
interface ResponsiveContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  deviceType: DeviceType;
  orientation: 'portrait' | 'landscape';
  currentBreakpoint: Breakpoint;
  belowBreakpoint: (breakpoint: Breakpoint) => boolean;
  aboveBreakpoint: (breakpoint: Breakpoint) => boolean;
}

// Create context with default values
const ResponsiveContext = createContext<ResponsiveContextType>({
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  deviceType: 'desktop',
  orientation: 'landscape',
  currentBreakpoint: 'xl',
  belowBreakpoint: () => false,
  aboveBreakpoint: () => true,
});

// Provider component
export const ResponsiveProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  
  // Define breakpoint checks using MUI's useMediaQuery
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));
  
  // Device type checks
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Get current breakpoint
  const getCurrentBreakpoint = (): Breakpoint => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    return 'xl';
  };
  
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>('xl');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');
  
  // Detect orientation
  useEffect(() => {
    const updateOrientation = () => {
      setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape');
    };
    
    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    
    return () => {
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);
  
  // Update current breakpoint
  useEffect(() => {
    setCurrentBreakpoint(getCurrentBreakpoint());
  }, [isXs, isSm, isMd, isLg, isXl]);
  
  // Get device type
  const deviceType: DeviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  
  // Pre-compute common breakpoint queries
  const belowSmQuery = useMediaQuery(theme.breakpoints.down('sm'));
  const belowMdQuery = useMediaQuery(theme.breakpoints.down('md'));
  const belowLgQuery = useMediaQuery(theme.breakpoints.down('lg'));
  const belowXlQuery = useMediaQuery(theme.breakpoints.down('xl'));
  
  const aboveSmQuery = useMediaQuery(theme.breakpoints.up('sm'));
  const aboveMdQuery = useMediaQuery(theme.breakpoints.up('md'));
  const aboveLgQuery = useMediaQuery(theme.breakpoints.up('lg'));
  const aboveXlQuery = useMediaQuery(theme.breakpoints.up('xl'));
  
  // Helper functions for breakpoint comparison that use pre-computed values
  const belowBreakpoint = (breakpoint: Breakpoint): boolean => {
    switch (breakpoint) {
      case 'xs': return true; // Always below or at xs
      case 'sm': return belowSmQuery;
      case 'md': return belowMdQuery;
      case 'lg': return belowLgQuery;
      case 'xl': return belowXlQuery;
      default: return false;
    }
  };
  
  const aboveBreakpoint = (breakpoint: Breakpoint): boolean => {
    switch (breakpoint) {
      case 'xs': return aboveSmQuery; // Above xs means at least sm
      case 'sm': return aboveMdQuery;
      case 'md': return aboveLgQuery;
      case 'lg': return aboveXlQuery;
      case 'xl': return false; // Nothing is above xl
      default: return false;
    }
  };
  
  const value = {
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    orientation,
    currentBreakpoint,
    belowBreakpoint,
    aboveBreakpoint,
  };
  
  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
};

// Custom hook for using the responsive context
export const useResponsive = () => useContext(ResponsiveContext);

// Hook for responsive spacing that maintains consistent ratios across breakpoints
export const useResponsiveSpacing = () => {
  const { deviceType } = useResponsive();
  
  return {
    // Returns a multiplier based on device type
    getSpacing: (baseSize: number) => {
      const multipliers = {
        mobile: 0.75,
        tablet: 1,
        desktop: 1.25,
      };
      
      return baseSize * multipliers[deviceType];
    },
    
    // Predefined spacing values for common use cases
    spacing: {
      xs: deviceType === 'mobile' ? 0.5 : deviceType === 'tablet' ? 0.75 : 1,
      sm: deviceType === 'mobile' ? 1 : deviceType === 'tablet' ? 1.5 : 2,
      md: deviceType === 'mobile' ? 2 : deviceType === 'tablet' ? 3 : 4,
      lg: deviceType === 'mobile' ? 3 : deviceType === 'tablet' ? 4.5 : 6,
      xl: deviceType === 'mobile' ? 4 : deviceType === 'tablet' ? 6 : 8,
    },
  };
};

// Hook for responsive typography
export const useResponsiveTypography = () => {
  const { deviceType } = useResponsive();
  
  return {
    // Get font size based on base size and device type
    getFontSize: (baseSize: number) => {
      const multipliers = {
        mobile: 0.85,
        tablet: 0.95,
        desktop: 1,
      };
      
      return `${baseSize * multipliers[deviceType]}rem`;
    },
    
    // Predefined font sizes
    fontSize: {
      h1: deviceType === 'mobile' ? '1.75rem' : deviceType === 'tablet' ? '2.25rem' : '2.5rem',
      h2: deviceType === 'mobile' ? '1.5rem' : deviceType === 'tablet' ? '1.75rem' : '2rem',
      h3: deviceType === 'mobile' ? '1.25rem' : deviceType === 'tablet' ? '1.5rem' : '1.75rem',
      h4: deviceType === 'mobile' ? '1.125rem' : deviceType === 'tablet' ? '1.25rem' : '1.5rem',
      h5: deviceType === 'mobile' ? '1rem' : deviceType === 'tablet' ? '1.125rem' : '1.25rem',
      h6: deviceType === 'mobile' ? '0.875rem' : deviceType === 'tablet' ? '1rem' : '1.125rem',
      subtitle1: deviceType === 'mobile' ? '0.875rem' : deviceType === 'tablet' ? '0.9375rem' : '1rem',
      subtitle2: deviceType === 'mobile' ? '0.8125rem' : deviceType === 'tablet' ? '0.875rem' : '0.9375rem',
      body1: deviceType === 'mobile' ? '0.875rem' : deviceType === 'tablet' ? '0.9375rem' : '1rem',
      body2: deviceType === 'mobile' ? '0.8125rem' : deviceType === 'tablet' ? '0.875rem' : '0.9375rem',
      caption: deviceType === 'mobile' ? '0.75rem' : deviceType === 'tablet' ? '0.8125rem' : '0.875rem',
    },
  };
};

// Touch target utilities
export const touchTargetStyles = {
  // Minimum touch target size should be 44x44px per WCAG
  minTouchTargetSize: {
    minWidth: '44px',
    minHeight: '44px',
  },
  
  // For button-like elements
  buttonTouchTarget: {
    padding: '12px 16px',
    minHeight: '48px',
  },
  
  // For icon buttons
  iconTouchTarget: {
    padding: '12px',
    minWidth: '48px',
    minHeight: '48px',
  },
};
